import cx from 'classnames';
import Image, { ImageLoaderProps, ImageProps } from 'next/image';

export interface GlImageProps extends ImageProps {
  sysId?: string;
  heroImage?: boolean;
  contentType?: string | null;
}

export const imageLoader = ({ src, width }: ImageLoaderProps) =>
  `https:${src}?w=${width}&fm=avif`;

export function GlImage({
  src,
  sysId,
  contentType,
  heroImage,
  className,
  layout = 'responsive',
  loading = process.env.NEXT_PUBLIC_ENVIRONMENT === 'percy' ? 'eager' : 'lazy',
  style,
  ...props
}: GlImageProps) {
  if (!src) return null;

  let defaultSizes = null;

  switch (layout) {
    case 'fill':
      defaultSizes = '100vw';
      break;
    case 'responsive':
      if (props.width) {
        defaultSizes = `(max-width: ${props.width}px) 100vw, ${props.width}px`;
      } else {
        defaultSizes = '100vw';
      }
      break;
    case 'intrinsic':
    case 'fixed':
    default:
      if (props.width) {
        defaultSizes = `${props.width}px`;
      } else {
        defaultSizes = '100vw';
      }
      break;
  }

  // Normalize legacy props to next/image
  const {
    objectFit,
    width: pWidth,
    height: pHeight,
    ...imgProps
  } = (props as any) || {};
  const commonStyle = objectFit
    ? ({ objectFit, ...style } as React.CSSProperties)
    : style;
  const fillProps = layout === 'fill' ? { fill: true as const } : {};

  if (heroImage) {
    return (
      <Image
        {...imgProps}
        src={src}
        loader={imageLoader}
        className={className}
        priority={true}
        fetchPriority="high"
        data-testid="gl-hero-img"
        data-cfid={sysId}
        {...fillProps}
        {...(layout !== 'fill' ? { width: pWidth, height: pHeight } : {})}
        sizes={defaultSizes}
        style={{
          zIndex: 0,
          ...commonStyle
        }}
      />
    );
  }

  switch (contentType) {
    case src && 'image/svg+xml':
      return (
        <div className="flex h-full w-full flex-col items-center justify-center">
          <object
            className={cx(
              'h-[20rem] w-full small:h-[22rem] mobile:h-[25.875rem] tablet:h-[29.6875rem] desktop:h-[39.375rem]',
              className
            )}
            data={src as string}
            type={contentType}
            data-script-order={0}
            data-testid="svg-image-type"
            data-cfid={sysId}
          >
            <Image
              {...imgProps}
              src={src}
              loader={imageLoader}
              className={className}
              loading={loading}
              sizes={defaultSizes}
              data-testid="gl-img"
              {...fillProps}
              {...(layout !== 'fill' ? { width: pWidth, height: pHeight } : {})}
              data-cfid={sysId}
              style={{
                ...commonStyle
              }}
            />
          </object>
        </div>
      );

    case 'image/gif':
      return (
        <Image
          {...imgProps}
          src={src}
          loader={imageLoader}
          className={className}
          loading={loading}
          {...fillProps}
          {...(layout !== 'fill' ? { width: pWidth, height: pHeight } : {})}
          sizes={defaultSizes}
          data-testid="gl-img"
          data-cfid={sysId}
          style={{
            width: '100%',
            ...commonStyle
          }}
        />
      );

    case src && 'image/svg+xml+only':
    case 'image/jpeg':
    case 'image/png':
    default:
      return (
        <Image
          {...imgProps}
          src={src}
          loader={imageLoader}
          className={className}
          loading={loading}
          {...fillProps}
          {...(layout !== 'fill' ? { width: pWidth, height: pHeight } : {})}
          sizes={defaultSizes}
          data-testid="gl-img"
          data-cfid={sysId}
          style={{
            ...commonStyle
          }}
        />
      );
  }
}

export default GlImage;
